<template>
  <div class="day-section mb-1 rounded overflow-hidden">
    <div class="day-title px-3 py-2 progress-bar-striped">
      <font-awesome-icon icon="fa-solid fa-calendar" class="text-white"/> {{ label }}
    </div>
    <div class="day-grid">
      <div class="day-grid-inner d-flex flex-column">
        <div v-if="isLoading" class="loading-spinner text-center m-3">Ładowanie rezerwacji</div>

        <!-- Header row: hours in half-hour intervals -->
        <div v-if="!isLoading" class="grid-header d-flex">
          <div class="corner-cell cell-label text-center">Stan.</div>
          <div
              v-for="slot in timeSlots"
              :key="slot"
              class="time-cell text-center"
          >
             {{ slot }}
          </div>
        </div>

        <!-- Rows: one per space -->
        <div
            v-for="(space, spaceIdx) in spaces"
            :key="space"
            class="grid-row d-flex position-relative"
            :class="{ 'row-alt': spaceIdx % 2 === 1 }"
        >
          <div class="space-cell cell-label text-center">
            <font-awesome-icon icon="fa-solid fa-bullseye"/> {{ space.name }}
          </div>

          <!-- Wrapper for slots to allow correct absolute positioning of the button -->
          <div class="d-flex flex-grow-1 position-relative">
            <div
                v-for="(slot, slotIdx) in timeSlots"
                :key="slot"
                class="slot-cell d-flex align-items-center justify-content-center"
                :class="slotClass(spaceIdx, slotIdx)"
                @click="onSlotClick(spaceIdx, slotIdx)"
                @mouseenter="onSlotMouseEnter(spaceIdx, slotIdx)"
                @mouseleave="hoveredReservationId = null"
            >
              <span class="slot-placeholder"><small>{{ slot }}</small></span>
            </div>

            <button
                v-if="selectedRange && selectedRange.spaceIdx === spaceIdx"
                class="reserve-btn btn btn-sm"
                :style="reserveButtonStyle"
                @click.stop="openReservationModal"
            >
              Zarezerwuj {{ timeSlots[selectedRange.from] }} - {{ getEndSlotTime(selectedRange.to) }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Reservation confirmation modal moved to Teleport -->
    <Teleport to="body">
      <div
          v-if="showModal && selectedRange"
          class="modal-overlay"
          @click.self="closeReservationModal"
      >
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Potwierdzenie rezerwacji</h5>
            <button
                type="button"
                class="btn-close btn-close-white"
                @click="closeReservationModal"
                :disabled="isSubmitting"
            ></button>
          </div>
          <div v-if="!reservationSuccess" class="modal-body">
            <p><strong>Stanowisko:</strong> {{ spaces[selectedRange.spaceIdx].name }}</p>
            <p><strong>Data:</strong> {{ label }}</p>
            <p><strong>Czas:</strong> {{ timeSlots[selectedRange.from] }} - {{ getEndSlotTime(selectedRange.to) }}</p>
            <p><strong>Cena:</strong> {{ reservationPrice }} zł</p>


            <div class="mt-3">
              <label class="form-label text-white-50 small d-block mb-2">Metoda płatności</label>
              <div
                  v-for="method in availablePaymentMethods"
                  :key="method"
                  class="form-check payment-radio-item mb-2 p-0"
              >
                <input
                    class="btn-check"
                    type="radio"
                    :name="'paymentMethod'"
                    :id="'payment_' + method"
                    :value="method"
                    v-model="selectedPaymentMethod"
                    :disabled="isSubmitting"
                    autocomplete="off"
                >
                <label
                    class="btn btn-outline-secondary w-100 text-start d-flex align-items-center py-2 px-3"
                    :for="'payment_' + method"
                >
                  <font-awesome-icon
                      :icon="selectedPaymentMethod === method ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle'"
                      class="me-2"
                      :class="selectedPaymentMethod === method ? 'text-warning' : 'text-secondary'"
                  />
                  {{ formatPaymentMethod(method) }}
                </label>
              </div>
            </div>
<!--            <div class="mt-3">
              <label for="paymentMethod" class="form-label text-white-50 small">Metoda płatności</label>
              <select
                  id="paymentMethod"
                  v-model="selectedPaymentMethod"
                  class="form-select bg-dark text-white border-secondary"
                  :disabled="isSubmitting"
              >
                <option value="" disabled>Wybierz metodę płatności</option>
                <option v-for="method in availablePaymentMethods" :key="method" :value="method">
                  {{ formatPaymentMethod(method) }}
                </option>
              </select>
            </div>-->

            <div v-if="submissionError" class="alert alert-danger mt-3">
              {{ submissionError }}
            </div>
          </div>
          <div v-else class="modal-body text-center py-4">
            <font-awesome-icon icon="fa-solid fa-circle-check" class="text-success mb-3" size="4x" />
            <h4 class="text-white">Rezerwacja udana!</h4>
            <p class="text-secondary mb-0">Twoje miejsce zostało zarezerwowane.</p>
          </div>
          <div v-if="!reservationSuccess" class="modal-footer">
            <button
                type="button"
                class="btn btn-secondary"
                @click="closeReservationModal"
                :disabled="isSubmitting"
            >
              Anuluj
            </button>
            <button
                type="button"
                class="btn btn-primary"
                @click="confirmReservation"
                :disabled="isSubmitting"
            >
              <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              Zarezerwuj
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <ReservationDetails
        ref="reservationDetailsModal"
        :show="showDetailsModal"
        :reservation-id="selectedReservationId"
        :space-name="selectedSpaceName"
        @close="closeDetailsModal"
        @reservation-cancelled="handleReservationCancelled"
    />

    <FullPageAlert
        v-if="errorAlert.show"
        :title="errorAlert.title"
        :message="errorAlert.message"
        @close="errorAlert.show = false"
    />
  </div>
</template>

<script>
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import FullPageAlert from "@/components/notifications/FullPageAlert.vue";
import ReservationDetails from "@/components/reservations/ReservationDetails.vue";
import { usePricesStore } from "@/stores/prices";
import { fetchWrapper } from "@/wrappers/api/fetchWrapper";

export default {
  name: 'DaySection',
  components: {
    FontAwesomeIcon,
    FullPageAlert,
    ReservationDetails
  },
  props: {
    index: {
      type: Number,
      required: true
    },
    label: {
      type: String,
      required: true,
    },
    spaces: {
      type: Array,
      required: true,
    },
    timeSlots: {
      type: Array,
      required: true,
    },
    isLoading: {
      type: Boolean,
      required: true,
    },
    cooldownMinutes: {
      type: Number,
      default: 0
    },
    // New props for shared selection state
    externalFirstCell: {
      type: Object,
      default: null
    },
    externalSelectedRange: {
      type: Object,
      default: null
    },
    activeSectionLabel: {
      type: String,
      default: null
    }
  },
  setup() {
    const pricesStore = usePricesStore();
    return { pricesStore };
  },
  data() {
    return {
      showModal: false,
      showDetailsModal: false,
      selectedReservationId: null,
      selectedSpaceName: null,
      selectedReservationDetails: null,
      hoveredReservationId: null,
      currentTime: this.getCurrentHHMM(),
      timer: null,
      isSubmitting: false,
      reservationSuccess: false,
      submissionError: null,
      selectedPaymentMethod: '',
      errorAlert: {
        show: false,
        title: '',
        message: ''
      }
    }
  },
  computed: {
    midSlot() {
      if (!this.selectedRange) return null
      return Math.floor((this.selectedRange.from + this.selectedRange.to) / 2)
    },
    reserveButtonStyle() {
      if (!this.selectedRange) return {};

      const totalSlots = this.timeSlots.length;
      const startPercent = (this.selectedRange.from / totalSlots) * 100;
      const endPercent = ((this.selectedRange.to + 1) / totalSlots) * 100;
      const centerPercent = (startPercent + endPercent) / 2;

      return {
        left: `${centerPercent}%`,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        position: 'absolute',
        zIndex: 100 // Ensure it stays above all cells
      };
    },
    // Computed properties to handle local vs global selection
    isThisSectionActive() {
      return this.activeSectionLabel === this.label;
    },
    firstCell() {
      return this.isThisSectionActive ? this.externalFirstCell : null;
    },
    selectedRange() {
      return this.isThisSectionActive ? this.externalSelectedRange : null;
    },
    availablePaymentMethods() {
      // Using locationId 1 as static for now
      return this.pricesStore.getPaymentMethodsByLocationId(1);
    },
    reservationPrice() {
      if (!this.selectedRange || !this.pricesStore) return 0;

      const start = this.timeSlots[this.selectedRange.from];
      const end = this.getEndSlotTime(this.selectedRange.to);

      const [startH, startM] = start.split(':').map(Number);
      const [endH, endM] = end.split(':').map(Number);

      let totalMinutes = (endH * 60 + endM) - (startH * 60 + startM);

      // Handle cases where end time might be after midnight (if applicable)
      if (totalMinutes < 0) totalMinutes += 1440;

      const h = Math.floor(totalMinutes / 60);
      const m = totalMinutes % 60;
      const duration = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;

      // Call the getter as a function
      return this.pricesStore.calculatePrice(1, duration) || 0;
    }
  },
  mounted() {
    this.timer = setInterval(() => {
      this.currentTime = this.getCurrentHHMM();
    }, 60000);
  },
  unmounted() {
    if (this.timer) clearInterval(this.timer);
  },
  watch: {
    // Automatically close modal if selection is cleared or section becomes inactive
    selectedRange(newVal) {
      if (!newVal && this.showModal) {
        this.showModal = false;
      }
    },
    // Check if the current time makes the current selection invalid
    currentTime() {
      if (!this.isThisSectionActive) return;

      // Extract YYYY-MM-DD from label to check if this section is "today"
      const dateMatch = this.label.match(/(\d{2})\.(\d{2})\.(\d{4})/);
      if (!dateMatch) return;
      const dateKey = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;
      const todayKey = new Date().toISOString().split('T')[0];

      if (dateKey === todayKey) {
        // Check firstCell (pending selection)
        if (this.firstCell) {
          const slotTime = this.timeSlots[this.firstCell.slotIdx];
          if (slotTime < this.currentTime) {
            this.$emit('update-selection', { label: null, firstCell: null, selectedRange: null });
            return;
          }
        }

        // Check selectedRange (confirmed range)
        if (this.selectedRange) {
          // If any slot in the range [from, to] is now in the past
          // Usually checking 'from' is enough if range is always forward
          const startTime = this.timeSlots[this.selectedRange.from];
          if (startTime < this.currentTime) {
            this.$emit('update-selection', { label: null, firstCell: null, selectedRange: null });
          }
        }
      }
    }
  },
  methods: {
    onSlotMouseEnter(spaceIdx, slotIdx) {
      const userRes = this.getUserReservationAt(spaceIdx, slotIdx);
      if (userRes) {
        this.hoveredReservationId = userRes.uuid;
      } else {
        this.hoveredReservationId = null;
      }
    },
    getCurrentHHMM() {
      const now = new Date();
      const currentDay = now.getDate();
      const minutesMargin = 20;

      now.setMinutes(now.getMinutes() + minutesMargin);

      // If adding the margin pushed the date to the next day, cap it at 23:59
      if (now.getDate() !== currentDay) {
        return '23:59';
      }

      return now.getHours().toString().padStart(2, '0') + ':' +
          now.getMinutes().toString().padStart(2, '0');
    },
    onSlotClick(spaceIdx, slotIdx) {
      const userRes = this.getUserReservationAt(spaceIdx, slotIdx);
      if (userRes) {
        this.selectedSpaceName = this.spaces[spaceIdx]?.name;
        this.showDetailsModal = true;
        this.selectedReservationId = userRes.uuid;
        return;
      }

      if (this.isReserved(spaceIdx, slotIdx)) return

      // If clicking a new cell and nothing was selected, or if a range was already finished,
      // or if we clicked in a different section entirely: start fresh.
      if (!this.isThisSectionActive || this.selectedRange || this.firstCell === null) {
        this.$emit('update-selection', {
          label: this.label,
          firstCell: { spaceIdx, slotIdx },
          selectedRange: null
        });
        return
      }

      // If we are here, this.firstCell is guaranteed to exist because of the check above
      const { spaceIdx: firstSpace, slotIdx: firstSlot } = this.firstCell

      if (spaceIdx === firstSpace && slotIdx > firstSlot) {
        // Check if any slot in the selected range is already reserved
        for (let i = firstSlot; i <= slotIdx; i++) {
          if (this.isReserved(spaceIdx, i)) {
            this.errorAlert = {
              show: true,
              title: 'Błąd wyboru',
              message: 'Nie można wykonać rezerwacji na już zarezerwowanych godzinach!'
            };
            this.$emit('update-selection', { label: null, firstCell: null, selectedRange: null });
            return
          }
        }
        this.$emit('update-selection', {
          label: this.label,
          firstCell: null,
          selectedRange: { spaceIdx: firstSpace, from: firstSlot, to: slotIdx }
        });
      } else {
        // If clicked same cell, a cell before, or a different row: start a new selection at the clicked point
        this.$emit('update-selection', {
          label: this.label,
          firstCell: { spaceIdx, slotIdx },
          selectedRange: null
        });
      }
    },

    slotClass(spaceIdx, slotIdx) {
      const userRes = this.getUserReservationAt(spaceIdx, slotIdx);
      if (userRes) {
        let classes = 'slot-user-reserved';
        if (this.hoveredReservationId === userRes.uuid) {
          classes += ' slot-user-reserved-hover';
        }
        return classes;
      }

      if (this.isReserved(spaceIdx, slotIdx)) {
        return 'slot-reserved'
      }

      if (this.firstCell && this.firstCell.spaceIdx === spaceIdx && this.firstCell.slotIdx === slotIdx) {
        return 'slot-pending'
      }

      if (
          this.selectedRange &&
          this.selectedRange.spaceIdx === spaceIdx &&
          slotIdx >= this.selectedRange.from &&
          slotIdx <= this.selectedRange.to
      ) {
        if (slotIdx === this.selectedRange.from) return 'slot-selected slot-selected-start'
        if (slotIdx === this.selectedRange.to) return 'slot-selected slot-selected-end'
        return 'slot-selected'
      }

      return ''
    },

    openReservationModal() {
      this.reservationSuccess = false;
      this.submissionError = null;
      this.selectedPaymentMethod = this.availablePaymentMethods[0] || '';
      this.showModal = true
    },

    closeReservationModal() {
      if (this.isSubmitting) return;
      this.showModal = false
      this.reservationSuccess = false;
    },

    closeDetailsModal() {
      this.showDetailsModal = false;
      this.selectedReservationId = null;
      this.selectedSpaceName = null;
    },

    async confirmReservation() {
      if (!this.selectedRange || this.isSubmitting) return

      this.isSubmitting = true;
      this.submissionError = null;

      try {
        // Extract YYYY-MM-DD from label (e.g., "Poniedziałek, 06.07.2026")
        const dateMatch = this.label.match(/(\d{2})\.(\d{2})\.(\d{4})/);
        if (!dateMatch) throw new Error("Nieprawidłowy format daty");

        const dateStr = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;
        const startTime = this.timeSlots[this.selectedRange.from];
        const endTime = this.getEndSlotTime(this.selectedRange.to);

        const body = {
          space_id: this.spaces[this.selectedRange.spaceIdx].id,
          start_date: `${dateStr}T${startTime}:00+02:00`,
          end_date: `${dateStr}T${endTime}:00+02:00`,
          paid: this.selectedPaymentMethod
        };

        await fetchWrapper.post('/reservations/space/create', body);

        this.reservationSuccess = true;
        this.$emit('update-selection', { label: null, firstCell: null, selectedRange: null });
        this.$emit('reservation-created'); // Notify parent to refresh data

        // Close modal after 5 seconds
        setTimeout(() => {
          this.showModal = false;
          this.reservationSuccess = false;
        }, 5000);

      } catch (err) {
            this.submissionError = err;
      } finally {
        this.isSubmitting = false;
      }
    },

    getEndSlotTime(slotIdx) {
      // If there is a next slot, that is our end time
      if (this.timeSlots[slotIdx + 1]) {
        return this.timeSlots[slotIdx + 1];
      }

      // Otherwise, calculate +30 mins from the last slot manually
      const lastSlot = this.timeSlots[slotIdx]; // e.g., "21:30"
      const [h, m] = lastSlot.split(':').map(Number);
      const date = new Date();
      date.setHours(h, m + 30, 0, 0);

      const hh = String(date.getHours()).padStart(2, '0');
      const mm = String(date.getMinutes()).padStart(2, '0');
      return `${hh}:${mm}`;
    },

    getUserReservationAt(spaceIdx, slotIdx) {
      const space = this.spaces[spaceIdx];
      if (!space || !space.userReservations) return null;

      const dateMatch = this.label.match(/(\d{2})\.(\d{2})\.(\d{4})/);
      if (!dateMatch) return null;
      const dateKey = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;
      const slotTime = this.timeSlots[slotIdx];

      const dayUserReservations = space.userReservations[dateKey];
      if (!dayUserReservations) return null;

      return dayUserReservations.find(res => {
        const start = new Date(res.start_date);
        const end = new Date(res.end_date);

        const formatLocalTime = (d) => {
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          return `${hh}:${mm}`;
        };

        const startTime = formatLocalTime(start);
        const endTime = formatLocalTime(end);

        return slotTime >= startTime && slotTime < endTime;
      });
    },

    isReserved(spaceIdx, slotIdx) {
      const space = this.spaces[spaceIdx]
      if (!space || !space.reservations) return false

      // Extract YYYY-MM-DD from label (e.g., "Poniedziałek, 06.07.2026")
      const dateMatch = this.label.match(/(\d{2})\.(\d{2})\.(\d{4})/)
      if (!dateMatch) return false
      const dateKey = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`

      const slotTime = this.timeSlots[slotIdx] // e.g. "10:30"

      // Check if the slot is in the past for today
      const now = new Date()
      const todayKey = now.toISOString().split('T')[0]
      if (dateKey === todayKey) {
        if (slotTime < this.currentTime) {
          return true
        }
      }

      const dayReservations = space.reservations[dateKey]
      if (!dayReservations) return false

      return dayReservations.some(res => {
        const startD = new Date(res.start_date);
        const endD = new Date(res.end_date);

        // Apply cooldown: subtract from start, add to end
        const startWithCooldown = new Date(startD.getTime() - this.cooldownMinutes * 60000);
        const endWithCooldown = new Date(endD.getTime() + this.cooldownMinutes * 60000);

        const formatLocalTime = (d) => {
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          return `${hh}:${mm}`;
        };

        const start = formatLocalTime(startWithCooldown);
        const end = formatLocalTime(endWithCooldown);

        return slotTime >= start && slotTime < end
      })
    },

    isUserReserved(spaceIdx, slotIdx) {
      const space = this.spaces[spaceIdx];
      if (!space || !space.userReservations) return false;

      const dateMatch = this.label.match(/(\d{2})\.(\d{2})\.(\d{4})/);
      if (!dateMatch) return false;
      const dateKey = `${dateMatch[3]}-${dateMatch[2]}-${dateMatch[1]}`;
      const slotTime = this.timeSlots[slotIdx];

      const dayUserReservations = space.userReservations[dateKey];
      if (!dayUserReservations) return false;

      return dayUserReservations.some(res => {
        const start = new Date(res.start_date);
        const end = new Date(res.end_date);

        const formatLocalTime = (d) => {
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          return `${hh}:${mm}`;
        };

        const startTime = formatLocalTime(start);
        const endTime = formatLocalTime(end);

        return slotTime >= startTime && slotTime < endTime;
      });
    },

    formatPaymentMethod(method) {
      const translations = {
        'pay_at_location': 'Płatność na miejscu',
        'p24': 'Przelewy24 (Online)'
      };
      return translations[method] || method;
    },

    handleReservationCancelled(){
      this.$emit('reservation-created'); // Notify parent to refresh dat
    }
  },
}
</script>

<style scoped>
/* ── Card shell ─────────────────────────────────────────── */
.day-section {
  width: 100%;
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  backdrop-filter: blur(4px);
}

/* ── Day title ──────────────────────────────────────────── */
.day-title {
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #f0c040;
  background-color: var(--bs-tertiary-bg);
  border-bottom: 1px solid var(--bs-border-color);
  border-left: 3px solid #f0c040;
}

[data-bs-theme="dark"] .day-title {
  background-color: rgba(0, 0, 0, 0.4);
}

[data-bs-theme="light"] .day-title {
  background-color: #7b7b7b;
  color: white;
  text-shadow: 0 0 5px black;
}

/* ── Grid container ─────────────────────────────────────── */
.day-grid {
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.15) transparent;
}

.day-grid::-webkit-scrollbar {
  height: 4px;
}

.day-grid::-webkit-scrollbar-track {
  background: transparent;
}

.day-grid::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.15);
  border-radius: 2px;
}

.day-grid::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.day-grid-inner {
  min-width: max-content;
  width: 100%;
}

/* ── Shared: fixed-width label column ───────────────────── */
.cell-label {
  flex: 0 0 70px;
  box-sizing: border-box;
  padding: 0.35rem 0.75rem;
  font-size: 0.984rem;
  font-weight: 500;
  color: var(--bs-body-color);
  background-color: var(--bs-secondary-bg);
  border-right: 1px solid var(--bs-border-color);
}

[data-bs-theme="dark"] .cell-label {
  background-color: #1c1c1c;
}

[data-bs-theme="light"] .cell-label {
  color:white;
  background-color: #424242;
}

/* ── Time header cells ──────────────────────────────────── */
.time-cell {
  flex: 1 0 60px;
  box-sizing: border-box;
  padding: 0.15rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--bs-secondary-color);
  background-color: var(--bs-secondary-bg);
  border-right: 1px solid var(--bs-border-color);
  border-bottom: 1px solid var(--bs-border-color);
}

[data-bs-theme="dark"] .time-cell {
  background-color: #03191e;
}

[data-bs-theme="light"] .time-cell {
  background-color: #a2a2a2;;
}

/* ── Space rows ─────────────────────────────────────────── */
.grid-row {
  border-top: 1px solid var(--bs-border-color-translucent);
  overflow: visible;
}

.row-alt .slot-cell {
  background-color: var(--bs-tertiary-bg);
}

[data-bs-theme="dark"] .row-alt .slot-cell {
  background-color: rgba(0, 0, 0, 0.4);
}

[data-bs-theme="light"] .row-alt .slot-cell {
  color: var(--bs-body-color);
  background-color: rgb(200 200 200 / 0.35);
}

[data-bs-theme="light"] .row-alt .slot-cell .slot-placeholder {
  color: rgb(92 92 92 / 0.73)
}

/* ── Slot cells ─────────────────────────────────────────── */
.slot-cell {
  flex: 1 0 60px;
  box-sizing: border-box;
  min-height: 36px;
  cursor: pointer;
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  transition: background-color 0.15s ease, box-shadow 0.15s ease;
}

[data-bs-theme="dark"] .slot-cell {
  background-color: rgb(28 28 28 / 0.4);
}

[data-bs-theme="light"] .slot-cell .slot-placeholder {
  color: rgb(128 128 128 / 0.58)
}

[data-bs-theme="dark"] .slot-cell:hover:not(.slot-user-reserved,.slot-reserved) {
  background-color: rgba(240, 192, 64, 0.15) !important;
  box-shadow: inset 0 0 0 1px rgba(240, 192, 64, 0.35);
}

[data-bs-theme="dark"] .slot-cell:hover:not(.slot-user-reserved,.slot-reserved) .slot-placeholder {
  color: rgba(240, 192, 64, 1);
}

[data-bs-theme="light"] .slot-cell:hover:not(.slot-user-reserved,.slot-reserved) {
  background-color: rgb(16 136 255 / 0.15) !important;
  box-shadow: inset 0 0 0 1px rgb(64 93 240 / 0.35);
}

[data-bs-theme="light"] .slot-cell:hover:not(.slot-user-reserved,.slot-reserved) .slot-placeholder {
  color: rgb(48 48 48);
}

/* ── Pending first selection ────────────────────────────── */
[data-bs-theme="dark"] .slot-pending {
  background-color: rgba(240, 192, 64, 0.25) !important;
  box-shadow: inset 0 0 0 1px rgba(240, 192, 64, 0.6);
}

[data-bs-theme="dark"] .slot-pending .slot-placeholder {
  color: rgba(240, 192, 64, 0.8);
}

[data-bs-theme="light"] .slot-pending {
  background-color: rgb(16 136 255 / 0.15) !important;
  box-shadow: inset 0 0 0 1px rgb(64 93 240 / 0.35);
}

[data-bs-theme="light"] .slot-pending .slot-placeholder {
  color: rgb(46 46 46);
}

/* ── User Reserved slots (Green) ───────────────────────── */
.slot-user-reserved {
  background: repeating-linear-gradient(
      45deg,
      #2e7d32 0px,
      #2e7d32 10px,
      #43a047 60px,
      #43a047 40px
  );
  background-size: 50px 1px;
  animation: stripes 2s linear infinite;
  cursor: default;
}

@keyframes stripes {
  from {
    background-position: 0 0;
  }
  to {
    background-position: 50px 1px;
  }
}

.slot-user-reserved-hover {
  background: repeating-linear-gradient(
      45deg,
      #65ca6b 0px,
      #61ba65 10px,
      #65bc67 60px,
      #72df76 40px
  );
  background-size: 40px 1px;
  cursor: pointer !important;
}

[data-bs-theme="dark"] .slot-user-reserved .slot-placeholder {
  color: rgb(255 255 255 / 0.8);
}

[data-bs-theme="light"] .slot-user-reserved .slot-placeholder, [data-bs-theme="light"] .row-alt .slot-user-reserved .slot-placeholder {
  color: rgb(255 255 255 / 0.8);
}

/* ── Reserved slots ────────────────────────────────────── */
.slot-reserved {
  background-color: rgb(223 76 76 / 0.82) !important;
  cursor: not-allowed;
}

.slot-reserved .slot-placeholder {
  text-decoration: line-through;
}

[data-bs-theme="dark"] .slot-reserved .slot-placeholder, [data-bs-theme="light"] .slot-reserved .slot-placeholder, [data-bs-theme="dark"] .row-alt .slot-reserved .slot-placeholder, [data-bs-theme="light"] .row-alt .slot-reserved .slot-placeholder {
  color: rgb(67 8 8);
}

/* ── Confirmed selection range ──────────────────────────── */
.slot-selected {
  background-color: rgba(240, 192, 64, 0.2) !important;
}

[data-bs-theme="light"] .slot-selected {
  background-color: rgb(16 136 255 / 0.15) !important;
}

.slot-selected .slot-placeholder {
  color: rgb(255 255 255 / 0.7);
}

[data-bs-theme="light"] .slot-selected .slot-placeholder {
  color: rgb(19 19 19 / 0.7);
}

.slot-selected-start {
  box-shadow: inset 2px 0 0 0 rgba(240, 192, 64, 0.8), inset 0 2px 0 0 rgba(240, 192, 64, 0.8), inset 0 -2px 0 0 rgba(240, 192, 64, 0.8);
}

[data-bs-theme="light"] .slot-selected-start {
  box-shadow: inset 2px 0 0 0 rgb(58 178 230 / 0.8), inset 0 2px 0 0 rgb(58 178 230 / 0.8), inset 0 -2px 0 0 rgb(58 178 230 / 0.8);
}

.slot-selected-end {
  box-shadow: inset -2px 0 0 0 rgba(240, 192, 64, 0.8), inset 0 2px 0 0 rgba(240, 192, 64, 0.8), inset 0 -2px 0 0 rgba(240, 192, 64, 0.8);
}

[data-bs-theme="light"] .slot-selected-end {
  box-shadow: inset -2px 0 0 0 rgb(58 178 230 / 0.8), inset 0 2px 0 0 rgb(58 178 230 / 0.8), inset 0 -2px 0 0 rgb(58 178 230 / 0.8);
}

/* ── Placeholder text ───────────────────────────────────── */
.slot-placeholder {
  font-size: 1.008rem;
  color: var(--bs-border-color);
  user-select: none;
  pointer-events: none;
  transition: color 0.15s ease;
}

/* ── Reserve button ─────────────────────────────────────── */
.reserve-btn {
  position: absolute;
  z-index: 10;
  white-space: nowrap;
  padding: 1rem 1rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: #fff;
  background: linear-gradient(135deg, #e1c359 0%, #c59824 100%);
  border: none;
  box-shadow: 0 4px 12px rgba(240, 192, 64, 0.3);
  transition: all 0.2s ease;
  text-shadow: 0px 0px 5px black;
}

.reserve-btn:hover {
  background: linear-gradient(135deg, #ffd966 0%, #f0c040 100%);
  box-shadow: 0 6px 16px rgba(240, 192, 64, 0.5);
  transform: scale(1.05);
}

.reserve-btn:active {
  transform: scale(0.95);
}

/* ── Modal overlay ──────────────────────────────────────── */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-content {
  background-color: var(--bs-body-bg);
  border: 1px solid var(--bs-border-color);
  backdrop-filter: blur(8px);
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
}

.modal-header {
  padding: 1.25rem;
  border-bottom: 1px solid var(--bs-border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #f0c040;
  margin: 0;
}

.modal-body {
  padding: 1.25rem;
  color: var(--bs-body-color);
}

.modal-body p {
  margin-bottom: 0.75rem;
  font-size: 0.95rem;
}

.modal-body strong {
  color: var(--bs-heading-color, inherit);
}

.modal-footer {
  padding: 1rem 1.25rem;
  border-top: 1px solid var(--bs-border-color);
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
}

.btn-secondary {
  background-color: var(--bs-secondary-bg) !important;
  border: 1px solid var(--bs-border-color) !important;
  color: var(--bs-secondary-color) !important;
}

.btn-secondary:hover {
  background-color: rgba(255, 255, 255, 0.15) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.btn-primary {
  background-color: #f0c040 !important;
  border: none !important;
  color: #000 !important;
  font-weight: 600;
}

.btn-primary:hover {
  background-color: #ffd966 !important;
}

.btn-close-white {
  filter: brightness(0.8);
}

.btn-close-white:hover {
  filter: brightness(1);
}
</style>